/**
 * My Relationships — the Phase 1 primary screen.
 *
 * "This screen alone should support most daily relationship management." Everything here serves
 * that: the triad is in the grid, and Status / Next Move / Follow-Up / Owner / Priority are
 * editable IN PLACE. Requiring a profile round trip per change would make Tidalfin slower than
 * the spreadsheet it replaces.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { tfApi, type RelationshipPatch, type RelationshipQuery } from '../api/tf_api'
import { useApp } from '../contexts/app_context'
import { PageHeader } from '../components/layout/app_shell'
import { NewPersonModal } from '../components/ui/new_person_modal'
import { InlineEditCell } from '../components/ui/inline_edit_cell'
import {
  Avatar,
  ConfirmDialog,
  DueBadge,
  EmptyState,
  ErrorState,
  ModalShell,
  Spinner,
  StatusBadge,
  Tag,
  formatShortDate,
  todayIso,
} from '../components/ui/primitives'
import type { RelationshipRow } from '../types/tf_types'

type ChipKey = 'all' | 'needs_action' | 'warm' | 'waiting' | 'dormant' | 'talent'

const CHIPS: Array<{ key: ChipKey; label: string; query: RelationshipQuery }> = [
  { key: 'all', label: 'All', query: {} },
  { key: 'needs_action', label: 'Needs action', query: { needs_action: true } },
  { key: 'warm', label: 'Warm & Strong', query: { strength: 'Warm,Strong' } },
  { key: 'waiting', label: 'Waiting on them', query: { waiting_only: true } },
  { key: 'dormant', label: 'Going dormant', query: { dormant_days: 90, strength: 'Warm,Strong' } },
  { key: 'talent', label: 'Talent', query: { talent_only: true } },
]

export function MyRelationshipsPage() {
  const { options, notify, notifyError, config } = useApp()
  const [searchParams, setSearchParams] = useSearchParams()

  const chip = (searchParams.get('view') as ChipKey) || 'all'
  const search = searchParams.get('q') ?? ''
  const page = Number(searchParams.get('page') ?? '1')
  const sort = searchParams.get('sort') ?? 'NextFollowUpDate'
  const desc = searchParams.get('desc') === '1'

  const [rows, setRows] = useState<RelationshipRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<{ message: string; technical?: string } | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [nextMoveFor, setNextMoveFor] = useState<RelationshipRow | null>(null)

  const pageSize = Number(config?.ui?.default_page_size ?? 25)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const chipQuery = CHIPS.find((c) => c.key === chip)?.query ?? {}
      const result = await tfApi.fetchRelationships({
        ...chipQuery,
        q: search || undefined,
        sort,
        desc,
        page,
        page_size: pageSize,
      })
      setRows(result.Items)
      setTotal(result.Total)
    } catch (err: any) {
      setError({ message: err?.detail?.message ?? String(err), technical: err?.detail?.technical })
    } finally {
      setLoading(false)
    }
  }, [chip, search, sort, desc, page, pageSize])

  useEffect(() => {
    void load()
  }, [load])

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value === null || value === '') next.delete(key)
    else next.set(key, value)
    if (key !== 'page') next.delete('page')
    setSearchParams(next)
  }

  /**
   * Commit one inline edit.
   *
   * Optimistic in the cell, authoritative here: the row is patched locally so the grid does not
   * flicker, and the server's response replaces it so anything the service derived (DoNotContact
   * following the strength, for instance) shows immediately rather than after a reload.
   */
  const patchRow = useCallback(
    async (contactId: number, patch: RelationshipPatch) => {
      const saved = await tfApi.updateRelationship(contactId, patch)
      setRows((current) =>
        current.map((r) =>
          r.ContactId === contactId
            ? {
                ...r,
                RelationshipStrength: saved.RelationshipStrength ?? r.RelationshipStrength,
                ContactStatus: saved.ContactStatus ?? null,
                LeadStatus: saved.LeadStatus ?? null,
                NextMove: saved.NextMove ?? null,
                NextFollowUpDate: saved.NextFollowUpDate ?? null,
                OwnerUserId: saved.OwnerUserId ?? null,
                Priority: saved.Priority ?? null,
                WaitingForResponse: Boolean(saved.WaitingForResponse),
                DoNotContact: Boolean(saved.DoNotContact),
              }
            : r,
        ),
      )
    },
    [],
  )

  const commit = useCallback(
    (row: RelationshipRow, field: keyof RelationshipPatch, label: string) =>
      async (next: string | null) => {
        try {
          await patchRow(row.ContactId, { [field]: next } as RelationshipPatch)
          notify({ tone: 'ok', title: 'Saved', body: `${label} updated for ${row.DisplayName}` })
        } catch (err) {
          //  Surfaced, never a silent revert — the cell rolls back and the user is told why.
          notifyError(`Could not update ${label.toLowerCase()}`, err)
          throw err
        }
      },
    [patchRow, notify, notifyError],
  )

  const toggleSelected = (contactId: number) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(contactId)) next.delete(contactId)
      else next.add(contactId)
      return next
    })
  }

  const bulkAssignOwner = async (owner: string) => {
    const ids = [...selected]
    let ok = 0
    for (const id of ids) {
      try {
        await patchRow(id, { OwnerUserId: owner })
        ok += 1
      } catch {
        /* individual failures are reported in aggregate below */
      }
    }
    notify({
      tone: ok === ids.length ? 'ok' : 'err',
      title: 'Bulk owner assignment',
      body: `${ok} of ${ids.length} updated`,
    })
    setSelected(new Set())
  }

  const strengthOptions = useMemo(
    () => options('RELATIONSHIP_STRENGTH').map((v) => ({ value: v.ValueLabel, label: v.ValueLabel })),
    [options],
  )
  const statusOptions = useMemo(
    () => options('CONTACT_STATUS').map((v) => ({ value: v.ValueLabel, label: v.ValueLabel })),
    [options],
  )
  const leadStatusOptions = useMemo(
    () => options('LEAD_STATUS').map((v) => ({ value: v.ValueLabel, label: v.ValueLabel })),
    [options],
  )
  const navigate = useNavigate()
  const [newPersonOpen, setNewPersonOpen] = useState(false)
  const [removing, setRemoving] = useState<RelationshipRow | null>(null)

  const reassign = useCallback(
    (row: RelationshipRow) => async (next: string | null) => {
      try {
        const result = await tfApi.setPersonOrganization(row.ContactId, next)
        notify({
          tone: 'ok',
          title: result.OrganizationCreated ? 'Organization created' : 'Organization changed',
          body: result.OrganizationName
            ? result.OrganizationCreated
              ? `${result.OrganizationName} did not exist, so it was created and linked.`
              : `${row.DisplayName} now works at ${result.OrganizationName}.`
            : `${row.DisplayName} is no longer linked to a company.`,
        })
        await load()
      } catch (err) {
        notifyError('Could not change the organization', err)
        throw err
      }
    },
    [load, notify, notifyError],
  )

  const remove = useCallback(async (row: RelationshipRow) => {
    setRemoving(null)
    try {
      const result = await tfApi.deletePerson(row.ContactId)
      notify({
        tone: 'ok',
        title: `${result.DisplayName} removed`,
        body: result.CancelledFollowUps
          ? `${result.CancelledFollowUps} open follow-up${result.CancelledFollowUps === 1 ? ' was' : 's were'} cancelled. Their history is kept.`
          : 'Their activity history is kept.',
      })
      await load()
    } catch (err) {
      notifyError('Could not remove this person', err)
    }
  }, [load, notify, notifyError])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <>
      <PageHeader
        crumb="Tidalfin / Home / My Relationships"
        title="My Relationships"
        description="Every relationship, with where it stands and what happens next. Click any Status, Next Move, Follow-Up, Owner or Priority cell to edit it in place."
        actions={
          <>
            <button className="btn secondary" type="button" onClick={() => void load()}>
              <span className="material-symbols-outlined">refresh</span>Refresh
            </button>
            {/* WAS A LINK TO THIS PAGE. It carried a person_add icon, promised creation and
                navigated to the screen you were already on — so on /relationships/people it did
                nothing at all, which is exactly how it was reported. */}
            <Link className="btn secondary" to="/data/imports">
              <span className="material-symbols-outlined">upload_file</span>Import
            </Link>
            <button className="btn primary" type="button" onClick={() => setNewPersonOpen(true)}>
              <span className="material-symbols-outlined">person_add</span>New Person
            </button>
          </>
        }
      />

      <section className="page-toolbar">
        <div className="chip-row">
          {CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`chip${chip === c.key ? ' on' : ''}`}
              onClick={() => setParam('view', c.key === 'all' ? null : c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="toolbar-spacer" />
        <div className="global-search" style={{ maxWidth: 300 }}>
          <span className="material-symbols-outlined" aria-hidden="true">search</span>
          <input
            type="search"
            defaultValue={search}
            placeholder="Filter these relationships…"
            aria-label="Filter relationships"
            onKeyDown={(e) => {
              if (e.key === 'Enter') setParam('q', (e.target as HTMLInputElement).value)
            }}
          />
        </div>
      </section>

      <div className="body-scroll" id="tf-content">
        {error && <ErrorState title="Could not load relationships" message={error.message} technical={error.technical} onRetry={() => void load()} />}

        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="diversity_3"
            title="No relationships match"
            message={search ? `Nothing matches “${search}”.` : 'Try a different filter, or import your contacts.'}
          />
        ) : (
          <div className="table-wrap">
            <div className="table-scroll">
              <table className="dt">
                <caption className="sr-only">
                  Relationships with type, strength, status, last contact, next move and follow-up.
                  Status, next move, follow-up, owner and priority are editable in place.
                </caption>
                <thead>
                  <tr>
                    <th className="checkbox-cell" scope="col">
                      <input
                        type="checkbox"
                        aria-label="Select all rows"
                        checked={selected.size > 0 && selected.size === rows.length}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(rows.map((r) => r.ContactId)) : new Set())
                        }
                      />
                    </th>
                    {[
                      ['DisplayName', 'Person'],
                      ['OrganizationName', 'Organization'],
                      ['', 'Type'],
                      ['RelationshipStrength', 'Relationship'],
                      ['LeadStatus', 'Opportunity'],
                      ['LastInteractionAt', 'Last Contact'],
                      ['', 'Status'],
                      ['', 'Next Move'],
                      ['NextFollowUpDate', 'Follow-Up'],
                      ['', 'Owner'],
                      ['', ''],
                    ].map(([key, label], columnIndex) => (
                      <th
                        key={`${label}-${columnIndex}`}
                        scope="col"
                        className={key ? 'sortable' : undefined}
                        aria-sort={sort === key ? (desc ? 'descending' : 'ascending') : undefined}
                        onClick={() => {
                          if (!key) return
                          if (sort === key) setParam('desc', desc ? null : '1')
                          else {
                            setParam('sort', key)
                          }
                        }}
                      >
                        {label}
                        {key === sort && (
                          <span className="material-symbols-outlined">
                            {desc ? 'arrow_downward' : 'arrow_upward'}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.ContactId} className={selected.has(row.ContactId) ? 'selected' : undefined}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${row.DisplayName}`}
                          checked={selected.has(row.ContactId)}
                          onChange={() => toggleSelected(row.ContactId)}
                        />
                      </td>
                      <td>
                        <div className="person-cell">
                          <Avatar name={row.DisplayName} />
                          <div>
                            <Link className="person-name" to={`/relationships/people/${row.ContactId}`}>
                              {row.DisplayName}
                            </Link>
                            <div className="person-sub">{row.JobTitle || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ minWidth: 170 }}>
                        {/* Editable in place, and an unknown name CREATES the company. People
                            change jobs, and the new employer is usually not already an account —
                            a picker limited to existing ones makes the common case impossible. */}
                        <InlineEditCell
                          value={row.OrganizationName}
                          placeholder="Set organization"
                          ariaLabel={`Organization for ${row.DisplayName}`}
                          onCommit={reassign(row)}
                        />
                      </td>
                      <td>
                        <span className="person-sub">
                          {row.RelationshipTypes.length ? row.RelationshipTypes.join(' · ') : '—'}
                        </span>
                      </td>
                      <td>
                        <InlineEditCell
                          value={row.RelationshipStrength}
                          type="select"
                          options={strengthOptions}
                          placeholder="Set strength"
                          ariaLabel={`Relationship strength for ${row.DisplayName}`}
                          onCommit={commit(row, 'RelationshipStrength', 'Relationship strength')}
                          render={(v) => <StatusBadge domain="RELATIONSHIP_STRENGTH" value={v} />}
                        />
                      </td>
                      <td>
                        <InlineEditCell
                          value={row.LeadStatus}
                          type="select"
                          options={leadStatusOptions}
                          placeholder="Set lead status"
                          ariaLabel={`Lead status for ${row.DisplayName}`}
                          onCommit={commit(row, 'LeadStatus', 'Lead status')}
                          render={(v) => (v ? <Tag>{v}</Tag> : null)}
                        />
                      </td>
                      <td>
                        <span className="person-sub">{formatShortDate(row.LastInteractionAt)}</span>
                      </td>
                      <td>
                        <InlineEditCell
                          value={row.ContactStatus}
                          type="select"
                          options={statusOptions}
                          placeholder="Set status"
                          ariaLabel={`Status for ${row.DisplayName}`}
                          onCommit={commit(row, 'ContactStatus', 'Status')}
                        />
                      </td>
                      <td style={{ minWidth: 220 }}>
                        {/* Next Move is italic on every screen — the one free-text value in a grid
                            of structured ones, and the one the eye must find first. */}
                        <InlineEditCell
                          value={row.NextMove}
                          type="longtext"
                          displayClass="next-move"
                          placeholder="Set next move"
                          ariaLabel={`Next move for ${row.DisplayName}`}
                          onCommit={commit(row, 'NextMove', 'Next move')}
                        />
                      </td>
                      <td>
                        <InlineEditCell
                          value={row.NextFollowUpDate?.slice(0, 10) ?? null}
                          type="date"
                          placeholder="Set date"
                          ariaLabel={`Follow-up date for ${row.DisplayName}`}
                          onCommit={commit(row, 'NextFollowUpDate', 'Follow-up date')}
                          render={(v) => <DueBadge date={v} waiting={row.WaitingForResponse} />}
                        />
                      </td>
                      <td>
                        <InlineEditCell
                          value={row.OwnerUserId}
                          placeholder="Unassigned"
                          displayClass="person-sub"
                          ariaLabel={`Owner for ${row.DisplayName}`}
                          onCommit={commit(row, 'OwnerUserId', 'Owner')}
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {/* Edit opens the profile, where the whole record is editable. The grid
                            edits single values in place; a typo in a surname needs the form. */}
                        <Link
                          className="btn ghost sm"
                          to={`/relationships/people/${row.ContactId}`}
                          title={`Open ${row.DisplayName}`}
                          aria-label={`Open ${row.DisplayName}`}
                        >
                          <span className="material-symbols-outlined">edit</span>
                        </Link>
                        <button
                          className="btn ghost sm"
                          type="button"
                          title={`Remove ${row.DisplayName}`}
                          aria-label={`Remove ${row.DisplayName}`}
                          onClick={() => setRemoving(row)}
                        >
                          <span className="material-symbols-outlined">person_remove</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-foot">
              <span>
                Showing {rows.length} of {total} relationships
                {selected.size > 0 && ` · ${selected.size} selected`}
              </span>
              <div className="pager">
                <button type="button" disabled={page <= 1} onClick={() => setParam('page', String(page - 1))}>
                  Prev
                </button>
                <button type="button" className="on">
                  {page} / {totalPages}
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setParam('page', String(page + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {selected.size > 0 && (
          <div className="card" style={{ marginTop: 14 }}>
            <div className="card-body" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 900, color: 'var(--tf-brand)' }}>
                {selected.size} selected
              </span>
              <button
                className="btn secondary sm"
                type="button"
                onClick={() => {
                  const owner = window.prompt('Assign owner to the selected relationships:')
                  if (owner) void bulkAssignOwner(owner)
                }}
              >
                <span className="material-symbols-outlined">person</span>Assign owner
              </button>
              <button className="btn ghost sm" type="button" onClick={() => setSelected(new Set())}>
                Clear selection
              </button>
            </div>
          </div>
        )}
      </div>

      {nextMoveFor && (
        <NextMoveModal row={nextMoveFor} onClose={() => setNextMoveFor(null)} onDone={() => void load()} />
      )}
      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.DisplayName}?`}
          message={`They come off every list and their open follow-ups are cancelled. Their activities and notes are KEPT — that history is the record of what was actually done, and deleting it would leave the account with calls nobody can account for. This can be undone.`}
          confirmLabel="Remove"
          danger
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
      {newPersonOpen && (
        <NewPersonModal
          onClose={() => setNewPersonOpen(false)}
          onCreated={(contactId) => {
            //  Reload rather than optimistically splicing the row in. The grid is sorted and
            //  filtered server-side, so a locally inserted row would appear in the wrong place and
            //  survive a filter it does not match.
            void load()
            navigate(`/relationships/people/${contactId}`)
          }}
        />
      )}

    </>
  )
}

/**
 * Set next move + follow-up together.
 *
 * One call, because they are one decision: a next move with no date is a next move that will not
 * happen, and the service creates the follow-up in the same transaction.
 */
export function NextMoveModal({
  row,
  onClose,
  onDone,
}: {
  row: RelationshipRow
  onClose: () => void
  onDone: () => void
}) {
  const { options, notify, notifyError } = useApp()
  const [nextMove, setNextMove] = useState(row.NextMove ?? '')
  const [date, setDate] = useState(row.NextFollowUpDate?.slice(0, 10) ?? todayIso())
  const [type, setType] = useState('Call')
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      await tfApi.setNextMove(row.ContactId, {
        NextMove: nextMove || null,
        NextFollowUpDate: date || null,
        FollowUpType: type,
        CreateFollowUp: true,
      })
      notify({ tone: 'ok', title: 'Next move set', body: `${row.DisplayName} · follow-up ${date}` })
      onDone()
      onClose()
    } catch (err) {
      notifyError('Could not set the next move', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell
      title={`Next move — ${row.DisplayName}`}
      description="Sets the next move, replaces any superseded follow-up, and schedules the new one."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="form-label" htmlFor="nm-move">Next move</label>
          <textarea
            id="nm-move"
            className="form-input"
            rows={3}
            value={nextMove}
            onChange={(e) => setNextMove(e.target.value)}
            placeholder="Call Wednesday evening"
          />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="nm-date">Follow-up date</label>
          <input id="nm-date" className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field">
          <label className="form-label" htmlFor="nm-type">Follow-up type</label>
          <select id="nm-type" className="form-input" value={type} onChange={(e) => setType(e.target.value)}>
            {options('FOLLOWUP_TYPE').map((v) => (
              <option key={v.ValueCode} value={v.ValueLabel}>
                {v.ValueLabel}
              </option>
            ))}
          </select>
        </div>
      </div>
    </ModalShell>
  )
}
